<template>
  <v-app-bar :elevation="0" density="compact" class="standalone-topbar">
    <LogoTile :src="logoUrl" :alt="tenantName" :size="24" class="ml-2" />
    <span class="standalone-topbar__name">{{ tenantName }}</span>

    <v-spacer />

    <v-menu v-if="showOrgSwitcher">
      <template #activator="{ props }">
        <v-btn variant="text" size="small" v-bind="props" class="standalone-topbar__org">
          <LogoTile :src="currentOrgLogo" :alt="currentOrgName" :size="18" class="mr-2" />
          {{ currentOrgName }}
          <v-icon size="16" class="ml-1">expand_more</v-icon>
        </v-btn>
      </template>
      <v-list density="compact">
        <v-list-item
          v-for="org in session.accessibleOrgs"
          :key="org.id"
          :active="org.id === session.currentOrg?.id"
          :disabled="switching"
          @click="switchOrg(org)"
        >
          <template #prepend>
            <LogoTile :src="org.logo || org.icon || null" :alt="org.name" :size="18" />
          </template>
          <v-list-item-title>{{ org.name }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <span v-else-if="session.currentOrg" class="standalone-topbar__org-static">{{ session.currentOrg.name }}</span>

    <p v-if="switchError" class="standalone-topbar__error">{{ switchError }}</p>

    <v-btn
      icon
      variant="text"
      size="small"
      class="standalone-topbar__theme-toggle"
      :title="darkMode ? 'Switch to light mode' : 'Switch to dark mode'"
      @click="toggleTheme"
    >
      <v-icon size="20">{{ darkMode ? "light_mode" : "dark_mode" }}</v-icon>
    </v-btn>

    <v-menu>
      <template #activator="{ props }">
        <v-btn icon variant="text" size="small" class="standalone-topbar__user-btn" v-bind="props">
          <v-avatar size="26" color="primary">
            <span class="standalone-topbar__initial">{{ userInitial }}</span>
          </v-avatar>
        </v-btn>
      </template>
      <v-list density="compact">
        <v-list-item :title="userFullName" :subtitle="session.user?.emailAddress" />
        <v-divider />
        <v-list-item class="standalone-topbar__signout" title="Sign out" prepend-icon="logout" @click="session.signOut()" />
      </v-list>
    </v-menu>
  </v-app-bar>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useTheme } from "vuetify";
import { useSessionStore } from "@/stores/session";
import LogoTile from "./LogoTile.vue";
import type { UserOrganisation } from "@/tenant/types";

export default defineComponent({
  name: "TopBar",
  components: { LogoTile },
  setup() {
    const session = useSessionStore();
    const theme = useTheme();
    const switching = ref(false);
    const switchError = ref<string | null>(null);

    const tenantName = computed(() => session.tenant?.name || "MetaShip");
    const logoUrl = computed(() => {
      const branding = session.tenant?.branding;
      return (theme.global.current.value.dark ? branding?.logoDarkUrl : branding?.logoLightUrl) || null;
    });

    const showOrgSwitcher = computed(
      () => session.tenant?.kind === "PRODUCT" && session.accessibleOrgs.length > 1,
    );
    const currentOrgName = computed(() => session.currentOrg?.name || "");
    const currentOrgLogo = computed(() => session.currentOrg?.logo || session.currentOrg?.icon || null);

    const darkMode = computed(() => theme.global.current.value.dark);
    function toggleTheme(): void {
      // The single source of truth for dark/light — flips body[data-theme]
      // too, which some hosted modules key off directly.
      window.MetaShipGlobalState?.toggleTheme();
      const isDark = window.MetaShipGlobalState?.getState().darkMode ?? true;
      theme.change(isDark ? "dark" : "light");
    }

    async function switchOrg(org: UserOrganisation): Promise<void> {
      if (org.id === session.currentOrg?.id || switching.value) return;
      switching.value = true;
      switchError.value = null;
      try {
        await session.selectOrganisation(org);
      } catch (e) {
        switchError.value = e instanceof Error ? e.message : "Could not switch organisation — please try again.";
      } finally {
        switching.value = false;
      }
    }

    const userFullName = computed(() => {
      const u = session.user;
      return u ? `${u.firstname} ${u.surname}`.trim() || u.emailAddress : "";
    });
    const userInitial = computed(() => (session.user?.firstname || session.user?.emailAddress || "?").charAt(0).toUpperCase());

    return {
      session,
      tenantName,
      logoUrl,
      showOrgSwitcher,
      currentOrgName,
      currentOrgLogo,
      switching,
      switchError,
      switchOrg,
      darkMode,
      toggleTheme,
      userFullName,
      userInitial,
    };
  },
});
</script>

<style scoped>
.standalone-topbar {
  padding-left: var(--metaship-nav-width);
  transition: padding-left 150ms ease;
}
.standalone-topbar__name {
  font-weight: 700;
  font-size: 14px;
  margin-left: 10px;
  white-space: nowrap;
}
.standalone-topbar__org,
.standalone-topbar__org-static {
  font-size: 12px;
  margin-right: 8px;
}
.standalone-topbar__initial {
  color: #fff;
  font-weight: 700;
  font-size: 13px;
}
.standalone-topbar__error {
  color: rgb(var(--v-theme-error));
  font-size: 11px;
  margin: 0 8px;
}
</style>
